import React, { useMemo, useState } from "react";
import {
  Box,
  useTheme,
  Button,
  Typography,
  Chip,
  Stack,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { gql, useQuery } from "@apollo/client";
import { formatDisplayDate } from "../../utils/date";
import CreateLoanProductForm from "./CreateLoanProductForm";
import LoanProductReminderDialog from "./LoanProductReminderDialog";
import { GET_LOAN_PRODUCT_REMINDER_SCHEDULES } from "graphql/loans";

const GET_LOAN_PRODUCTS = gql`
  query {
    loanProducts {
      id
      name
      interestRate
      durationInMonths
      isActive
      createdDate
      createdBy
      updatedDate
      updatedBy
      groupId
    }
  }
`;

const formatKesPercent = (v) => `${Number(v ?? 0)}%`;

const LoanProductsTable = () => {
  const theme = useTheme();
  const { data, loading, error, refetch } = useQuery(GET_LOAN_PRODUCTS);
  const {
    data: remindersData,
    loading: remindersLoading,
    error: remindersError,
    refetch: refetchReminders,
  } = useQuery(GET_LOAN_PRODUCT_REMINDER_SCHEDULES);

  const [openCreate, setOpenCreate] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const indexedRows = useMemo(
    () => (data?.loanProducts || []).map((row, index) => ({ ...row, sequence: index + 1 })),
    [data]
  );
  const reminderByLoanProductId = useMemo(() => {
    const schedules = remindersData?.loanProductReminderSchedules || [];
    return schedules.reduce((acc, schedule) => {
      acc[String(schedule.loanProductId)] = schedule;
      return acc;
    }, {});
  }, [remindersData]);

  const columns = useMemo(
    () => [
      { field: "sequence", headerName: "#", width: 70, sortable: false, filterable: false },
      { field: "name", headerName: "Name", flex: 1.2 },
      {
        field: "interestRate",
        headerName: "Interest",
        flex: 0.65,
        valueFormatter: ({ value }) => formatKesPercent(value),
      },
      {
        field: "durationInMonths",
        headerName: "Duration (Months)",
        flex: 0.8,
      },
      {
        field: "isActive",
        headerName: "Active",
        flex: 0.5,
        renderCell: ({ value }) =>
          value ? <Chip size="small" color="success" label="Active" sx={{ fontWeight: 700 }} /> : <Chip size="small" variant="outlined" label="Inactive" sx={{ fontWeight: 700 }} />,
      },
      {
        field: "reminder",
        headerName: "Reminder",
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => {
          const schedule = reminderByLoanProductId[String(row.id)];
          if (!schedule) {
            return <Chip size="small" variant="outlined" label="Not set" />;
          }

          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                color={schedule?.isActive ? "success" : "default"}
                label={`${schedule.daysBeforeDue}d before`}
              />
            </Stack>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.9,
        sortable: false,
        renderCell: ({ row }) => (
          <Button size="small" variant="outlined" color="secondary" sx={{ textTransform: "none", fontWeight: 700 }} onClick={() => setSelectedProduct(row)}>
            {reminderByLoanProductId[String(row.id)] ? "Edit Reminder" : "Set Reminder"}
          </Button>
        ),
      },
      {
        field: "createdDate",
        headerName: "Created",
        flex: 0.8,
        valueFormatter: ({ value }) => formatDisplayDate(value),
      },
      {
        field: "updatedDate",
        headerName: "Updated",
        flex: 0.8,
        valueFormatter: ({ value }) => formatDisplayDate(value),
      },
    ],
    [reminderByLoanProductId]
  );

  if (error) return <p>Error: {error.message}</p>;

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h6" sx={{ color: theme.palette.secondary[100] }}>
            Loan Products
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.secondary[200] }}>
            Manage group loan products and set automated due-date reminders.
          </Typography>
          {remindersError ? (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
              Reminder schedules failed to load: {remindersError.message}
            </Typography>
          ) : null}
        </Box>

        <Button variant="contained" color="secondary" onClick={() => setOpenCreate(true)}>
          Create Loan Product
        </Button>
      </Box>

      <Box
        height="70vh"
        sx={{
          "& .MuiDataGrid-root": { border: `1px solid ${theme.palette.secondary[200]}2E`, borderRadius: "12px", overflow: "hidden" },
          "& .MuiDataGrid-cell": { borderBottom: `1px solid ${theme.palette.secondary[200]}14` },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: `1px solid ${theme.palette.secondary[200]}1F`,
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.light,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: `1px solid ${theme.palette.secondary[200]}1F`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.secondary[200]} !important`,
          },
        }}
      >
        <DataGrid
          loading={loading || remindersLoading}
          rows={indexedRows}
          getRowId={(row) => row.id}
          columns={columns}
          rowHeight={52}
        />
      </Box>

      <CreateLoanProductForm
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={async () => {
          await refetch();
          setOpenCreate(false);
        }}
      />

      <LoanProductReminderDialog
        open={Boolean(selectedProduct)}
        onClose={() => setSelectedProduct(null)}
        loanProduct={selectedProduct}
        schedule={
          selectedProduct ? reminderByLoanProductId[String(selectedProduct.id)] : null
        }
        onSaved={async () => {
          await refetchReminders();
          setSelectedProduct(null);
        }}
      />
    </Box>
  );
};

export default LoanProductsTable;
